import { useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Typography, tailwindOverride } from '@getsynapse/design-system';
import UserAvatar from 'Atoms/UserAvatar';
import { isUserStatusDisabled } from 'utils/functions';
import TruncatedTableColumn from 'Pages/ProjectsListPage/components/ActiveProjects/components/TruncatedTableColumn/TruncatedTableColumn';
import { DeletedUser } from 'Atoms/DeletedUser';

export type TableAvatarType = {
  avatar_url?: string;
  data: {
    firstName: string;
    lastName: string;
    email: string;
  };
  status?: string;
};

type TableAvatarProps = {
  user: TableAvatarType;
  'data-cy'?: string;
  to?: string;
};
export const TableAvatar = ({
  user,
  'data-cy': dataCy,
  to = '',
}: TableAvatarProps) => {
  const location = useLocation();
  const isUserDisabled = useMemo(
    () => isUserStatusDisabled(user.status),
    [user.status]
  );

  const nameClassName = tailwindOverride('text-primary font-semibold', {
    'text-neutral-light': isUserDisabled,
  });

  const fullName = useMemo(
    () => `${user.data.firstName} ${user.data.lastName}`,
    [user.data.firstName, user.data.lastName]
  );

  const userNameLabel = useMemo(
    () => (
      <Typography
        variant='body'
        className={tailwindOverride('truncate', nameClassName)}
      >
        {fullName}
      </Typography>
    ),
    [fullName, nameClassName]
  );

  const linkToWithFrom = useMemo(
    () => ({
      pathname: to,
      state: { from: `${location.pathname}${location.search}` },
    }),
    [to, location.pathname, location.search]
  );

  const userNameLabelWithLink = useMemo(
    () => (
      <NavLink
        to={linkToWithFrom}
        className={tailwindOverride(
          nameClassName,
          'no-underline hover:underline focus:underline',
          'cursor-pointer',
          'hover:text-purple-dark focus:text-purple-dark',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
          'hover:font-semibold focus:font-semibold'
        )}
        aria-disabled={isUserDisabled}
      >
        {fullName}
      </NavLink>
    ),
    [fullName, linkToWithFrom, nameClassName, isUserDisabled]
  );

  const useNameComponent = useMemo(() => {
    return to ? userNameLabelWithLink : userNameLabel;
  }, [to, userNameLabelWithLink, userNameLabel]);

  if (!fullName) return <DeletedUser />;

  return (
    <div
      className='flex items-start min-w-0'
      data-cy={dataCy || 'table-avatar'}
    >
      <UserAvatar user={user} />
      <div className='pl-2 min-w-0 flex-1 overflow-hidden'>
        <TruncatedTableColumn
          value={fullName}
          className='text-primary font-semibold'
          content={useNameComponent}
        />
        <TruncatedTableColumn
          value={user.data.email}
          className='text-neutral'
          content={
            <Typography variant='body2' className='text-neutral'>
              {user.data.email}
            </Typography>
          }
        />
      </div>
    </div>
  );
};

export default TableAvatar;
